import { type ComponentPropsWithoutRef, forwardRef, type ReactNode } from 'react';

import { cn } from '@/utils/cn';

export interface IconButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  /**
   * Required by the type, not by a lint rule. An unlabelled icon button is invisible to a screen
   * reader, and this app's copy button is the control a blind player needs most.
   */
  'aria-label': string;
  icon: ReactNode;
  size?: 'sm' | 'md';
  variant?: 'ghost' | 'surface';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, size = 'md', variant = 'ghost', className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        // Both sizes render a ≥44px hit area even when the glyph is 20px.
        'inline-flex size-11 shrink-0 items-center justify-center rounded-md',
        'transition-colors duration-150 motion-reduce:transition-none',
        'focus-visible:ring-focus-ring focus-visible:ring-2 focus-visible:ring-offset-2',
        'focus-visible:ring-offset-background outline-none',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variant === 'ghost'
          ? 'text-text hover:bg-surface-muted'
          : 'bg-surface text-text border-border hover:bg-surface-muted border',
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true" className={cn('inline-flex', size === 'sm' ? 'size-4' : 'size-5')}>
        {icon}
      </span>
    </button>
  );
});
